import { Box, Typography, Paper, Stack, useTheme } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import type { NearbyCategory } from "../data/propertyData";

interface NearbyPlacesProps {
    nearby: Record<string, NearbyCategory[]>;
}

// Mapeo de iconos para cada categoría
const categoryIcons: Record<string, any> = {
    Schools: SchoolIcon,
    Shopping: ShoppingBagIcon,
    Dining: RestaurantIcon,
};

export default function NearbyPlaces({ nearby }: NearbyPlacesProps) {
    const theme = useTheme();

    // Check if nearby is empty or all categories are empty
    const hasNearbyPlaces = Object.keys(nearby).length > 0 && 
        Object.values(nearby).some(category => Array.isArray(category) && category.length > 0);

    // Hide component if empty
    if (!hasNearbyPlaces) {
        return null;
    }

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, md: 3 },
                mt: 4,
                borderRadius: 3,
                backgroundColor: "background.paper",
                width: "100%",
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <LocationOnIcon
                    sx={{
                        fontSize: 32,
                        color: theme.palette.primary.main,
                    }}
                />
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                        color: theme.palette.primary.main,
                    }}
                >
                    Nearby Places
                </Typography>
            </Box>

            {/* Categories - Horizontal Grid (3 columns) */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    gap: 2,
                }}
            >
                {Object.entries(nearby).map(([category, places]) => {
                    const IconComponent = categoryIcons[category] || LocationOnIcon;

                    console.log('nearby',nearby)
                    return (
                        <Paper
                            key={category}
                            elevation={0}
                            sx={{
                                p: 2.5,
                                backgroundColor: "white",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            {/* Icon with golden background */}
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    backgroundColor: theme.palette.secondary.main,
                                    borderRadius: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 2,
                                }}
                            >
                                <IconComponent
                                    sx={{
                                        fontSize: 24,
                                        color: "white",
                                    }}
                                />
                            </Box>

                            {/* Category Title */}
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color: theme.palette.primary.main,
                                    mb: 2,
                                }}
                            >
                                {category}
                            </Typography>

                            {/* Places List */}
                            <Stack spacing={0}>
                                {places.map((place, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            py: 1.5,
                                            borderBottom: i < places.length - 1 ? "1px solid" : "none",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {place.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.secondary.main,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {place.distance}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    );
                })}
            </Box>
        </Paper>
    );
}
